import * as React from 'react';
import { cn } from '../../lib/cn';

/**
 * Textarea — shadcn multi-line text-field idiom, themed onto HofSync brand
 * tokens. The multi-line sibling of `input.tsx`, and the surface the chat
 * `Composer` is built on.
 *
 * Preflight is deliberately excluded from the DS Tailwind build (see CLAUDE.md),
 * so a native <textarea> keeps its UA appearance, border, focus outline AND the
 * OS resize grabber. We therefore neutralise that chrome per-component, exactly
 * as `input.tsx` does:
 *   - `tw:appearance-none` removes UA control styling,
 *   - `tw:resize-none` drops the OS resize grabber (the Composer auto-grows),
 *   - an explicit `tw:border tw:border-input` + `tw:bg-card` re-establish the
 *     field surface on the brand `--input` / `--card` tokens,
 *   - `tw:focus-visible:ring-2 tw:ring-ring` swaps the UA focus outline for the
 *     brand ring (`--ring`),
 *   - `tw:font-sans` resets the UA font-family — form controls do NOT inherit
 *     the document font, and the UA default for <textarea> is MONOSPACE (#1927).
 */
export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, rows = 1, ...props }, ref) => (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(
        'tw:flex tw:w-full tw:appearance-none tw:resize-none tw:rounded-2xl tw:border tw:border-input tw:bg-card tw:px-3 tw:py-2 tw:font-sans tw:text-sm tw:leading-relaxed tw:text-foreground tw:shadow-sm tw:transition-colors',
        'tw:placeholder:text-muted-foreground',
        'tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring',
        'tw:disabled:cursor-not-allowed tw:disabled:opacity-50',
        className
      )}
      {...props}
    />
  )
);
Textarea.displayName = 'Textarea';
